import React, { useEffect, useState } from 'react';

import {
    Button,
    ColContainer,
    RowContainer,
    SelectList,
    toast,
    Typography,
} from '../../../components/common';
import { SkeletonTable } from '../../../components/common/Skeletons';
import { useGetUser } from '../../../contexts/user';
import { GetAllRoutingProfiles } from '../../../services/Api/routingProfiles/useApiRoutingProfiles';
import {
    GetScheduleByUserGroupId,
    GetUserGroupById,
    GetUserGroupsByOwner,
} from '../../../services/Api/userGroups/useApiUserGroups';
import InstancesTable from '../../../components/common/instancesTable';
import TeamScheduleBreakdown from './teamScheduleBreakdown';
import { GetUsers } from '../../../services/Api/users/useApiUser';
import {
    DeleteUserFromGroup,
    AddUserToGroup,
} from '../../../services/Api/userGroups/useApiUserGroups';
import NewGroupModal from '../../../components/Dashboard/Modals/NewGroup';
import { GetInstances } from '../../../services/Api/instances/useApiInstances';
import ReactTooltip from 'react-tooltip';

const TeamDashboard = () => {
    const { instances, userDetails, isLoading } = useGetUser();
    const [team, setTeam] = useState(0);
    const [teams, setTeams] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [teamSchedule, setTeamSchedule] = useState([]);
    const [routingProfiles, setRoutingProfiles] = useState([]);
    const [teamsLoading, setTeamsLoading] = useState(true);
    const [showCreateGroupForm, setShowCreateGroupForm] = useState(false);
    const [allInstances, setAllInstances] = useState([]);

    const removeUserFromGroup = (groupId, email) => {
        setTeamsLoading(true);
        const data = {
            groupid: groupId,
            email: email,
        };
        DeleteUserFromGroup(data)
            .then((res) => {
                if (res.status !== 200) {
                    toast.error();
                } else {
                    GetUserGroupById({ id: groupId }).then((update) => {
                        const newTeams = teams.filter((t) => t.id !== groupId);
                        setTeams(newTeams.concat(update.data));
                    });
                }
            })
            .catch((err) => {
                toast.error();
                console.error(err);
            })
            .finally(() => {
                setTeamsLoading(false);
            });
    };

    const addUserToGroup = (groupId, email) => {
        setTeamsLoading(true);
        const data = {
            groupid: groupId,
            email: email,
        };
        AddUserToGroup(data)
            .then((res) => {
                if (res.status !== 200) {
                    toast.error();
                } else {
                    GetUserGroupById({ id: groupId }).then((update) => {
                        const newTeams = teams.filter((t) => t.id !== groupId);
                        setTeams(newTeams.concat(update.data));
                        GetScheduleByUserGroupId({ id: team.value })
                            .then((result) => {
                                if (result && result.data) {
                                    const grouped = makeTeamMember(result.data);
                                    setTeamSchedule(grouped);
                                    setTeamsLoading(false);
                                }
                            })
                            .catch(() => {
                                toast.error();
                                setTeamsLoading(false);
                            });
                    });
                }
            })
            .catch((err) => {
                toast.error();
                console.error(err);
            })
            .finally(() => {
                setTeamsLoading(false);
            });
    };

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [teamSchedule]);

    useEffect(() => {
        GetInstances().then((res) => setAllInstances(res.data));
    }, []);

    useEffect(() => {
        //Get users in OUR database. Potential users are excluded by user.user if
        GetUsers().then((uresult) => {
            setAllUsers(
                (uresult.data || [])
                    .filter((user) => !!user.user)
                    .map((user) => ({
                        email: user.user.email,
                        firstname: user.user.firstname,
                        surname: user.user.surname,
                        role: user.user.rolename,
                    }))
            );
        });
        GetUserGroupsByOwner({ email: userDetails.username }).then((ugResult) => {
            const newTeams = ugResult?.data || [];
            setTeams(newTeams);
            const defaultTeam = newTeams.length
                ? { value: newTeams[0].id, label: newTeams[0].name }
                : { value: undefined, label: undefined };
            setTeam(defaultTeam);
            setTeamsLoading(false);
        });
        GetAllRoutingProfiles()
            .then((rpResult) => {
                setRoutingProfiles(rpResult.data);
            })
            .catch((err) => {
                console.error(err);
                toast.error();
            });
    }, [userDetails.username]);

    useEffect(() => {
        if (team.value) {
            setTeamsLoading(true);
            GetScheduleByUserGroupId({ id: team.value })
                .then((result) => {
                    if (result && result.data) {
                        const grouped = makeTeamMember(result.data);
                        setTeamSchedule(grouped);
                        setTeamsLoading(false);
                    }
                })
                .catch(() => {
                    toast.error();
                    setTeamsLoading(false);
                });
        }
    }, [team]);

    function makeTeamMember(data) {
        return data.reduce(function (rv, x) {
            rv[x['email']] = rv[x['email']] || {
                email: x['email'],
                firstname: x['firstname'],
                surname: x['surname'],
                itinerary: [],
            };
            (rv[x['email']].itinerary = rv[x['email']].itinerary || []).push(x);
            return rv;
        }, {});
    }

    return (
        <ColContainer spacing="large">
            <ColContainer spacing="medium">
                <Typography variant="heading1.1">Available Instances</Typography>
                <InstancesTable instances={instances()} isLoading={isLoading} />
            </ColContainer>
            <RowContainer className="items-center justify-between">
                <Typography variant="heading1.1">Team Daily Schedule</Typography>
                <RowContainer spacing="medium" className="items-center">
                    {teams && teams.length ? (
                        <>
                            <Typography variant="heading2.2">Select a team:</Typography>
                            <SelectList
                                onChange={(e) => setTeam(e)}
                                value={team}
                                options={teams.map((team) => ({
                                    label: team.name,
                                    value: team.id,
                                }))}
                            />
                        </>
                    ) : (
                        <></>
                    )}
                    {showCreateGroupForm && (
                        <NewGroupModal
                            onSuccess={(newTeam) => setTeams([...teams, newTeam])}
                            closeForm={() => setShowCreateGroupForm(false)}
                        />
                    )}
                    <Button type="primary" onClick={() => setShowCreateGroupForm(true)}>
                        Create New Team
                    </Button>
                </RowContainer>
            </RowContainer>
            {teamsLoading ? (
                <SkeletonTable />
            ) : (
                <TeamScheduleBreakdown
                    routingProfiles={routingProfiles}
                    instances={allInstances}
                    schedule={teamSchedule}
                    team={team.value}
                    teams={teams}
                    allUsers={allUsers}
                    isLoading={teamsLoading}
                    addUserToGroup={addUserToGroup}
                    removeUserFromGroup={removeUserFromGroup}
                />
            )}
        </ColContainer>
    );
};

export default TeamDashboard;
